import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject, Observable, switchMap, timer 
} from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private http = inject(HttpClient);

  private previewsVisibleSubject: BehaviorSubject<boolean>;

  public previewsVisible: Observable<boolean>;

  private contentReloadSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  constructor() {
    const storedPreviewState = sessionStorage.getItem('previewsVisible') === 'true';
    this.previewsVisibleSubject = new BehaviorSubject<boolean>(
      storedPreviewState,
    );
    this.previewsVisible = this.previewsVisibleSubject.asObservable();
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = localStorage.getItem('team-benefits-sso-token');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    if (this.previewsEnabled()) {
      headers = headers.set('preview', 'true');
    }
    return headers;
  }

  private previewsEnabled() {
    return environment.enablePreview && this.previewsVisibleSubject.value;
  }

  showPreviews() {
    this.previewsVisibleSubject.next(true);
    sessionStorage.setItem('previewsVisible', 'true');
    this.contentReloadSubject.next();
  }

  hidePreviews() {
    this.previewsVisibleSubject.next(false);
    sessionStorage.setItem('previewsVisible', 'false');
    this.contentReloadSubject.next();
  }

  searchContent(query: string): Observable<any> {
    return this.contentReloadSubject.pipe(
      switchMap(() => {
        const headers = this.getHeaders();
        return this.http.get(
          `${environment.baseUrl}/api/search?query=${query}`,
          { headers },
        );
      }),
    );
  }

  getContentById(
    contentId: string,
    refreshRateInSeconds: number = 2,
  ): Observable<any> {
    const url = `${environment.baseUrl}/api/content/${contentId}`;

    return this.contentReloadSubject.pipe(
      switchMap(() => {
        const headers = this.getHeaders();
        if (this.previewsEnabled()) {
          return timer(0, refreshRateInSeconds * 1000).pipe(
            switchMap(() => this.http.get(url, { headers })),
          );
        }
        return this.http.get(url, { headers });
      }),
    );
  }

  getContentBySlug(
    contentType: string,
    slug: string,
    refreshRateInSeconds: number = 2,
  ): Observable<any> {
    const url = `${environment.baseUrl}/api/content/${contentType}/${slug}?include=3`;

    return this.contentReloadSubject.pipe(
      switchMap(() => {
        const headers = this.getHeaders();
        if (this.previewsEnabled()) {
          return timer(0, refreshRateInSeconds * 1000).pipe(
            switchMap(() => this.http.get(url, { headers })),
          );
        }
        return this.http.get(url, { headers });
      }),
    );
  }

  getContentByType(
    contentType: string,
    refreshRateInSeconds: number = 2,
  ): Observable<any> {
    const url = `${environment.baseUrl}/api/contents/${contentType}`;

    return this.contentReloadSubject.pipe(
      switchMap(() => {
        const headers = this.getHeaders();
        if (this.previewsEnabled()) {
          return timer(0, refreshRateInSeconds * 1000).pipe(
            switchMap(() => this.http.get(url, { headers })),
          );
        }
        return this.http.get(url, { headers });
      }),
    );
  }
}
